import { apiClient } from './api';

export interface GuestPlayer {
  id: string;
  display_name: string;
  connection_status: string;
  joined_at: string;
  last_seen_at: string;
  response_count: number;
  total_score: string;
  session_type: string;
  session_expires_at: string;
}

export interface LessonInfo {
  id: string;
  name: string;
  description: string;
  run_code: string;
  status: string;
  max_players: number;
  current_players: number;
  host: {
    display_name: string;
    avatar_url: string | null;
  };
  activities_count?: number;
}

export interface LessonActivity {
  id: string;
  type: string;
  title: string;
  description: string;
  status: string;
  time_limit: number;
  started_at: string | null;
  ends_at: string | null;
  allows_multiple_responses: boolean;
  player_has_responded: boolean;
  activity_data: Record<string, any>;
}

export interface LessonState {
  lesson: LessonInfo;
  player: GuestPlayer;
  current_activity: LessonActivity | null;
  session_valid: boolean;
}

interface JoinLessonResponse {
  player: GuestPlayer;
  lesson: LessonInfo;
  session_id: string;
  reconnected?: boolean;
  instructions?: {
    message: string;
    next_steps: string[];
    session_info: {
      duration: string;
      auto_disconnect: string;
    };
  };
}

class GuestPlayerService {
  private sessionId: string | null = null;
  private runCode: string | null = null;
  private browserFingerprint: string | null = null;
  private cable: any = null;
  private lessonChannel: any = null;
  private connectionId: number = 0;
  private eventListeners: Map<string, Set<Function>> = new Map();
  public currentPlayer: GuestPlayer | null = null;

  constructor() {
    this.sessionId = this.getSessionId();
    this.browserFingerprint = this.generateBrowserFingerprint();
  }

  /**
   * Get or generate session ID
   */
  getSessionId(): string {
    if (this.sessionId) return this.sessionId;

    let sessionId = localStorage.getItem('guest_session_id');
    if (!sessionId) {
      sessionId = this.generateSessionId();
      localStorage.setItem('guest_session_id', sessionId);
    }

    this.sessionId = sessionId;
    return sessionId;
  }

  /**
   * Generate a unique session ID
   */
  private generateSessionId(): string {
    return `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Generate browser fingerprint for session tracking
   */
  private generateBrowserFingerprint(): string {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx?.fillText('Guest fingerprint', 10, 10);

    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset(),
      canvas.toDataURL()
    ].join('|');

    // Simple hash function
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    return hash.toString(36);
  }

  /**
   * Get run information (replaces getLessonInfo)
   */
  async getLessonInfo(runCode: string): Promise<{ success: boolean; data?: { lesson: LessonInfo; can_join: boolean; requires_approval: boolean }; error?: string }> {
    try {
      const response = await apiClient.request<{ success: boolean; data: { lesson: LessonInfo; can_join: boolean; requires_approval: boolean }; error?: string }>('get', `/public/run/${runCode}/info`);
      return response as any;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get lesson information'
      };
    }
  }

  /**
   * Join run as guest player with retry logic
   */
  async joinLesson(runCode: string, displayName: string, retryCount = 0): Promise<{ success: boolean; data?: JoinLessonResponse; error?: string }> {
    const maxRetries = 3;

    try {
      const sessionId = this.getSessionId();

      const response = await fetch(`/api/v1/public/run/${runCode}/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': sessionId,
          // Add CSRF token for Rails
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({
          display_name: displayName,
          browser_fingerprint: this.browserFingerprint
        })
      });

      if (!response.ok) {
        // Handle different HTTP error codes
        if (response.status === 401) {
          this.clearSession();
          return { success: false, error: 'Session expired. Please try again.' };
        } else if (response.status === 404) {
          return { success: false, error: 'Run not found. Please check the join code.' };
        } else if (response.status === 422) {
          const errorData = await response.json();
          return { success: false, error: errorData.error || 'Invalid request.' };
        } else if (response.status >= 500) {
          if (retryCount < maxRetries) {
            await this.delay(1000 * (retryCount + 1)); // Exponential backoff
            return this.joinLesson(runCode, displayName, retryCount + 1);
          }
          return { success: false, error: 'Server error. Please try again later.' };
        }
      }

      const result = await response.json();

      if (result.success) {
        this.runCode = runCode;
        this.currentPlayer = result.data?.player || null;
        localStorage.setItem('guest_run_code', runCode);
        localStorage.setItem('guest_display_name', displayName);
        localStorage.setItem('guest_session_valid', 'true');
      }

      return result;
    } catch (error: any) {
      if (retryCount < maxRetries && this.isNetworkError(error)) {
        await this.delay(1000 * (retryCount + 1));
        return this.joinLesson(runCode, displayName, retryCount + 1);
      }

      return {
        success: false,
        error: this.getErrorMessage(error)
      };
    }
  }

  /**
   * Get current lesson state via run_code
   */
  async getLessonState(runCode?: string): Promise<{ success: boolean; data?: LessonState; error?: string }> {
    try {
      const code = runCode || this.runCode || localStorage.getItem('guest_run_code');
      if (!code) {
        return { success: false, error: 'No run code available' };
      }

      const sessionId = this.getSessionId();
      const response = await fetch(`/api/v1/public/run/${code}/state`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': sessionId,
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        }
      });

      const result = await response.json();

      // Update currentPlayer from lesson state
      if (result.success && result.data?.player) {
        this.currentPlayer = result.data.player;
      }

      return result;
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to get lesson state'
      };
    }
  }

  /**
   * Submit response to current activity
   */
  async submitResponse(runCode: string, activityId: string, responseData: Record<string, any>): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const sessionId = this.getSessionId();

      const response = await fetch(`/api/v1/public/run/${runCode}/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': sessionId,
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({
          activity_id: activityId,
          response_data: responseData
        })
      });

      const result = await response.json();
      return result;
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to submit response'
      };
    }
  }


  /**
   * Start a self-paced activity
   */
  async startSelfPacedActivity(runCode: string, activityId: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const sessionId = this.getSessionId();
      const response = await fetch(`/api/v1/public/run/${runCode}/self_paced/start_activity`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': sessionId,
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({ activity_id: activityId })
      });
      return await response.json();
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to start activity' };
    }
  }

  /**
   * Complete a self-paced activity
   */
  async completeSelfPacedActivity(runCode: string, activityId: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const sessionId = this.getSessionId();
      const response = await fetch(`/api/v1/public/run/${runCode}/self_paced/complete_activity`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': sessionId,
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({ activity_id: activityId })
      });
      return await response.json();
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to complete activity' };
    }
  }

  /**
   * Get self-paced progress for all activities
   */
  async getSelfPacedProgress(runCode: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const sessionId = this.getSessionId();
      const response = await fetch(`/api/v1/public/run/${runCode}/self_paced/progress`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': sessionId,
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        }
      });
      return await response.json();
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to get progress' };
    }
  }

  /**
   * Get end-of-lesson results: leaderboard, this player's rank/score, and
   * a per-activity recap. Used for the live/instructor-led completion screen.
   */
  async getLessonResults(runCode: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const sessionId = this.getSessionId();
      const response = await fetch(`/api/v1/public/run/${runCode}/results`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': sessionId,
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        }
      });
      return await response.json();
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to get results' };
    }
  }

  /**
   * Execute a shell command for a command_shell activity
   */
  async executeShellCommand(runCode: string, activityId: string, command: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const sessionId = this.getSessionId();
      const response = await fetch(`/api/v1/public/run/${runCode}/execute_command`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': sessionId,
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({ activity_id: activityId, command })
      });
      return await response.json();
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to execute command' };
    }
  }

  /**
   * HIBP breach check via public endpoint
   */
  async hibpBreachCheck(runCode: string, email: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const sessionId = this.getSessionId();
      const response = await fetch(`/api/v1/public/run/${runCode}/hibp/breach_check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': sessionId,
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({ email })
      });
      return await response.json();
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to check breaches' };
    }
  }

  /**
   * HIBP password range check via public endpoint
   */
  async hibpPasswordRange(runCode: string, hashPrefix: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      // Safety guard: only ever transmit a 5-char SHA-1 hash prefix (HIBP
      // k-anonymity). This makes it structurally impossible to send a raw
      // password (or a full hash) through this method by mistake.
      if (!/^[0-9a-fA-F]{5}$/.test(hashPrefix)) {
        return { success: false, error: 'Invalid password hash prefix' };
      }
      const sessionId = this.getSessionId();
      const response = await fetch(`/api/v1/public/run/${runCode}/hibp/password_range`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-ID': sessionId,
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        body: JSON.stringify({ hash_prefix: hashPrefix })
      });
      return await response.json();
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to check password' };
    }
  }

  /**
   * Get stored display name
   */
  getStoredDisplayName(): string | null {
    return localStorage.getItem('guest_display_name');
  }

  /**
   * Get stored run code
   */
  getStoredRunCode(): string | null {
    return localStorage.getItem('guest_run_code') || localStorage.getItem('guest_lesson_code');
  }

  /**
   * Check if user has an active guest session
   */
  hasActiveSession(): boolean {
    return !!(this.getSessionId() && this.getStoredRunCode());
  }

  /**
   * Connect to WebSocket for real-time updates via AnyCable
   */
  connectWebSocket(runCode: string): void {
    this.disconnectWebSocket();

    // Increment connection ID so stale async connections are discarded
    const myConnectionId = ++this.connectionId;

    // Import ActionCable dynamically
    import('@rails/actioncable').then(({ createConsumer }) => {
      // If a newer connect/disconnect was called while awaiting import, bail out
      if (this.connectionId !== myConnectionId) return;

      // Connect to Rails ActionCable server
      const wsUrl = import.meta.env.DEV
        ? 'ws://localhost:3000/cable'
        : '/cable';

      this.cable = createConsumer(wsUrl);

      this.lessonChannel = this.cable.subscriptions.create(
        {
          channel: 'GuestLessonChannel',
          session_id: this.getSessionId(),
          run_code: runCode
        },
        {
          connected: () => {
            this.emit('websocket_connected', { runCode });

            // Request initial lesson state
            this.requestLessonState();
          },

          disconnected: () => {
            this.emit('websocket_disconnected', { runCode });
          },

          rejected: () => {
            this.emit('websocket_rejected', { runCode });
          },

          received: (data: any) => {
            this.handleWebSocketMessage(data);
          }
        }
      );

      // Send heartbeat every 30 seconds to keep session alive
      const heartbeatInterval = setInterval(() => {
        if (this.lessonChannel && this.lessonChannel.consumer.connection.isOpen()) {
          this.lessonChannel.perform('heartbeat', {
            timestamp: Date.now(),
            request_state: false
          });
        } else {
          // Clear interval if connection is closed
          clearInterval(heartbeatInterval);
        }
      }, 30000);

    }).catch(() => {
      if (this.connectionId !== myConnectionId) return;
      this.emit('websocket_error', { error: 'Failed to initialize WebSocket' });
    });
  }

  /**
   * Disconnect from WebSocket
   */
  disconnectWebSocket(): void {
    // Bump connection ID to invalidate any pending async connections
    this.connectionId++;

    if (this.lessonChannel) {
      this.lessonChannel.unsubscribe();
      this.lessonChannel = null;
    }
    if (this.cable) {
      this.cable.disconnect();
      this.cable = null;
    }
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleWebSocketMessage(data: any): void {
    switch (data.type) {
      case 'lesson_state_update':
        this.emit('lesson_state_changed', data.data);
        break;
      case 'activity_started':
        // Handle both data and activity formats for backwards compatibility
        const activityData = data.activity || data.data || data;
        this.emit('activity_started', activityData);
        break;
      case 'activity_completed':
      case 'activity_auto_completed':
        this.emit('activity_completed', data.activity || data.data || data);
        break;
      case 'activity_paused':
        this.emit('activity_paused', data.activity || data.data || data);
        break;
      case 'activity_resumed':
        this.emit('activity_resumed', data.activity || data.data || data);
        break;
      case 'activity_time_extended':
      case 'activity_timer_reset':
        this.emit('activity_time_updated', data.activity || data.data || data);
        break;
      case 'lesson_completed':
        this.emit('lesson_completed', data.data || data);
        break;
      case 'lesson_paused':
        this.emit('lesson_paused', data.data || data);
        break;
      case 'lesson_resumed':
        this.emit('lesson_resumed', data.data || data);
        break;
      case 'response_submitted':
        this.emit('player_responded', data);
        break;
      case 'activity_fully_responded':
        this.emit('activity_complete', data);
        break;
      case 'player_status_change':
        this.emit('player_status_changed', data);
        break;
      case 'player_kicked':
        this.emit('player_kicked', data);
        // If this player was kicked, clear session and emit special event
        if (data.player_id === this.currentPlayer?.id) {
          this.clearSession();
          this.emit('player_kicked_self', {
            reason: data.reason,
            message: 'You have been removed from the lesson'
          });
        }
        break;
      case 'error':
        this.emit('websocket_error', data);
        break;
      default:
        this.emit('websocket_message', data);
    }
  }

  /**
   * Add event listener
   */
  on(event: string, callback: Function): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event)!.add(callback);
  }

  /**
   * Remove event listener
   */
  off(event: string, callback: Function): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.delete(callback);
    }
  }

  /**
   * Emit event to listeners
   */
  private emit(event: string, data: any): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data);
        } catch {
          // listener error
        }
      });
    }
  }

  /**
   * Request current lesson state via WebSocket
   */
  requestLessonState(): void {
    if (this.lessonChannel) {
      this.lessonChannel.perform('heartbeat', { request_state: true });
    }
  }

  /**
   * Session recovery - attempt to reconnect with existing session
   */
  async recoverSession(): Promise<{ success: boolean; runCode?: string; error?: string }> {
    const storedRunCode = this.getStoredRunCode();
    const storedDisplayName = this.getStoredDisplayName();
    const sessionValid = localStorage.getItem('guest_session_valid') === 'true';

    if (!storedRunCode || !storedDisplayName || !sessionValid) {
      this.clearSession();
      return { success: false, error: 'No valid session found' };
    }

    // Validate session by checking lesson state
    const stateResult = await this.getLessonState(storedRunCode);

    if (stateResult.success && stateResult.data?.session_valid) {
      this.runCode = storedRunCode;
      this.currentPlayer = stateResult.data?.player || null;
      return { success: true, runCode: storedRunCode };
    } else {
      // Session expired, try to rejoin
      const rejoinResult = await this.joinLesson(storedRunCode, storedDisplayName);

      if (rejoinResult.success) {
        return { success: true, runCode: storedRunCode };
      } else {
        this.clearSession();
        return { success: false, error: rejoinResult.error || 'Failed to recover session' };
      }
    }
  }

  /**
   * Utility function to delay execution
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Check if error is network-related (retriable)
   */
  private isNetworkError(error: any): boolean {
    return error.name === 'TypeError' ||
           error.message?.includes('Failed to fetch') ||
           error.message?.includes('Network request failed') ||
           error.code === 'NETWORK_ERROR';
  }

  /**
   * Get user-friendly error message
   */
  private getErrorMessage(error: any): string {
    if (this.isNetworkError(error)) {
      return 'Network connection error. Please check your internet connection.';
    }

    if (error.message?.includes('JSON')) {
      return 'Server response error. Please try again.';
    }

    return error.message || 'An unexpected error occurred. Please try again.';
  }

  /**
   * Enhanced session validation
   */
  async validateSession(runCode?: string): Promise<boolean> {
    try {
      const state = await this.getLessonState(runCode);
      const isValid = state.success && state.data?.session_valid === true;

      localStorage.setItem('guest_session_valid', isValid.toString());

      if (!isValid) {
        this.emit('session_invalid', { runCode: runCode || this.runCode });
      }

      return isValid;
    } catch (error) {
      localStorage.setItem('guest_session_valid', 'false');
      return false;
    }
  }

  /**
   * Auto-retry with exponential backoff
   */
  private async retryWithBackoff<T>(
    operation: () => Promise<T>,
    maxRetries: number = 3,
    baseDelay: number = 1000
  ): Promise<T> {
    let lastError: any;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error;

        if (attempt === maxRetries || !this.isNetworkError(error)) {
          break;
        }

        const delay = baseDelay * Math.pow(2, attempt);
        await this.delay(delay);
      }
    }

    throw lastError;
  }

  /**
   * Enhanced clear session with cleanup
   */
  clearSession(): void {
    // Disconnect WebSocket first
    this.disconnectWebSocket();

    // Clear local storage (both old and new keys)
    localStorage.removeItem('guest_session_id');
    localStorage.removeItem('guest_run_code');
    localStorage.removeItem('guest_lesson_code');
    localStorage.removeItem('guest_display_name');
    localStorage.removeItem('guest_session_valid');

    // Reset instance variables
    this.sessionId = null;
    this.runCode = null;
    this.currentPlayer = null;

    // Emit session cleared event
    this.emit('session_cleared', {});
  }
}

// Export singleton instance
export const guestPlayerService = new GuestPlayerService();
